import { FieldList, Field, Form } from '../../../../utils/forms/form';
import * as Validators from '../../../../utils/forms/validators';

import { AddClientLead, InquiryFTF, Inquiry } from '../../../../../types/inquiries';
import { formatWithTimezone, formatForNativeDateInput } from '../../../../core/date-formatter';
import { FurnishingChoices } from '../../../../../constants';

import {
  DesignationOtherValidator, SpecificPropertiesValidator, FtfRequiredFieldValidator,
  FloorAreaValidator, LotAreaValidator, ResidentialNonLotValidator,
  CommercialRequiredFieldValidator, ExistingBusinessRequiredFieldValidator,
  OperationalOtherValidator, RentalRequiredFieldValidator
} from './validators';


function getBedroomsValue(ftf: InquiryFTF) {
  for (let i = 5; i >= 1; i--) {
    if (ftf[`bedrooms${i}`]) {
      return i;
    }
  }

  if (ftf.bedroomsStudio) {
    return 0;
  }
}

function getFurnishingValues(ftf: InquiryFTF) {
  return FurnishingChoices.filter(choice => ftf[choice.k2Field])
    .map(choice => choice.value);
}

function getOperationalDaysValues(ftf: InquiryFTF) {
  const days: string[] = [];
  if (ftf.operatingMonday) days.push('Mondays');
  if (ftf.operatingTuesday) days.push('Tuesdays');
  if (ftf.operatingWednesday) days.push('Wednesdays');
  if (ftf.operatingThursday) days.push('Thursdays');
  if (ftf.operatingFriday) days.push('Fridays');
  if (ftf.operatingSaturday) days.push('Saturdays');
  if (ftf.operatingSunday) days.push('Sundays')

  return days;
}

function getBuildingClass(ftf: InquiryFTF) {
  if (ftf.classPremium) {
    return 'Premium';
  } else if (ftf.classA) {
    return 'Class A';
  } else if (ftf.classB) {
    return 'Class B';
  } else if (ftf.classC) {
    return 'Class C';
  }
}

export function createFormFields(ftf?: InquiryFTF) {
  const isEditing = !!ftf;

  ftf = ftf || {};
  const client = ftf.contact || {};
  const amenities = ftf.amenities || [];
  const locations = ftf.locations || {};
  const floorArea = ftf.floorArea || {};
  const lotArea = ftf.lotArea || {};
  const budget = ftf.budget || {};

  const fields: FieldList = {
    'clientFirstName': new Field([ Validators.required ], null, client.firstName),
    'clientLastName': new Field([ Validators.required ], null, client.lastName),
    'mobile': new Field([ Validators.required ], null, client.mobile),
    'email': new Field([ Validators.required ], null, client.email),
    'nationality': new Field([ Validators.required ], null, client.countryOrigin),
    'designation': new Field([ Validators.required ], null, client.designation),
    'designationOther': new Field([], [ DesignationOtherValidator ], client.designationOther),
    'source': new Field(isEditing ? [] : [ Validators.required ], null, null),
    'paymentTerm': new Field([ Validators.required ], null, ftf.paymentTerm),
    'receipts': new Field([ Validators.required ], null, !!ftf.receipts),

    // hasSpecificProperties and selectedProperties are specific to Add Client and
    // do not exist in the FTF.
    'hasSpecificProperties': new Field([], null, !isEditing),
    'selectedProperties': new Field([], [ SpecificPropertiesValidator ], null),

    'division': new Field([], [ FtfRequiredFieldValidator ], ftf.division || 'Residential'),
    'category': new Field([], [ FtfRequiredFieldValidator ], ftf.category || 'Sale'),
    'type': new Field([], [ FtfRequiredFieldValidator ], ftf.type),

    'minLeaseTerms': new Field([], [ RentalRequiredFieldValidator ], ftf.minLeaseTerms),

    // Residential-specific fields
    'bedrooms': new Field([], [ ResidentialNonLotValidator ], getBedroomsValue(ftf)),
    'furnishing': new Field([], [ ResidentialNonLotValidator ], getFurnishingValues(ftf)),
    'amenities': new Field([], [], amenities.filter(a => a.value).map(a => a.field)),

    // Commercial-specific fields
    'operationalDays': new Field([], [ CommercialRequiredFieldValidator ], getOperationalDaysValues(ftf)),
    'operationalOthers': new Field([], [ OperationalOtherValidator ], ftf.operationalOthers),
    'businessStatus': new Field([], [ CommercialRequiredFieldValidator ], ftf.businessStatus),
    'natureOfBusiness': new Field([], [ CommercialRequiredFieldValidator ], ftf.businessNature),
    'operationalHours': new Field([], [ CommercialRequiredFieldValidator ], ftf.operationalHours),

    'buildingClass': new Field([], [ CommercialRequiredFieldValidator ], getBuildingClass(ftf)),

    // "Existing business"-specific fields
    'clientCompanyName': new Field([], [ ExistingBusinessRequiredFieldValidator ], ftf.businessName),
    'clientBuildingName': new Field([], [ ExistingBusinessRequiredFieldValidator ], ftf.businessBuildingName),
    'clientFloorUnit': new Field([], null, ftf.businessBuildingUnit),
    'clientLocation': new Field([], null, ftf.businessBuildingLocation),

    'locations': new Field([], [ FtfRequiredFieldValidator ], locations.locations),
    'minFloorArea': new Field([], [ FloorAreaValidator ], floorArea.min),
    'maxFloorArea': new Field([], null, floorArea.max),
    'minLotArea': new Field([], [ LotAreaValidator ], lotArea.min),
    'maxLotArea': new Field([], null, lotArea.max),
    'minPrice': new Field([], null, budget.min),
    'maxPrice': new Field([], [ FtfRequiredFieldValidator ], budget.max),

    'viewingDate': new Field([], [ FtfRequiredFieldValidator ], formatForNativeDateInput(ftf.dateViewing)),
    'moveInDate': new Field([], [ FtfRequiredFieldValidator ], formatForNativeDateInput(ftf.dateMoveIn)),
  };

  return fields;
}

export function createFtfObject(form: Form) {
  const fields = form.fields;

  const inquiry: AddClientLead = {
    contact: {
      firstName: form.fields.clientFirstName.value,
      lastName: form.fields.clientLastName.value,
      mobile: form.fields.mobile.value,
      email: form.fields.email.value,
      designation: form.fields.designation.value,
      designationOther: form.fields.designationOther.value,
      countryOrigin: form.fields.nationality.value
    },
    source: parseInt(form.fields.source.value),
    receipts: form.fields.receipts.value ? 1 : 0,
    paymentTerm: form.fields.paymentTerm.value,
  };

  if (form.fields.hasSpecificProperties.value) {
    inquiry.listings = form.fields.selectedProperties.value;
  } else {
    inquiry.division = fields.division.value;
    inquiry.category = fields.category.value;
    inquiry.type = fields.type.value;

    if (fields.minLeaseTerms.value !== undefined) {
      inquiry.minLeaseTerms = fields.minLeaseTerms.value;
    }

    if (inquiry.division === 'Residential') {
      switch (fields.bedrooms.value) {
        case 0:
          inquiry.bedroomsStudio = 1;
          break;
        case 1:
          inquiry.bedrooms1 = 1;
          break;
        case 2:
          inquiry.bedrooms2 = 1;
          break;
        case 3:
          inquiry.bedrooms3 = 1;
          break;
        case 4:
          inquiry.bedrooms4 = 1;
          break;
        case 5:
          inquiry.bedrooms5 = 1;
          break;
      }

      const furnishing = fields.furnishing.value as string[];
      if (furnishing) {
        furnishing.forEach(value => {
          const choice = FurnishingChoices.find(choice => choice.label === value);
          if (choice) {
            inquiry[choice.k2Field] = 1;
          }
        });
      }

      const amenities = fields.amenities.value as string[];
      if (amenities) {
        inquiry.amenities = amenities.map(amenityLabel => ({ field: amenityLabel, value: 1 }));
      }
    } else {
      const operationalDays = form.fields.operationalDays.value as string[];
      operationalDays.forEach(day => {
        if (day === 'Mondays') inquiry.operatingMonday = 1;
        if (day === 'Tuesdays') inquiry.operatingTuesday = 1;
        if (day === 'Wednesdays') inquiry.operatingWednesday = 1;
        if (day === 'Thursdays') inquiry.operatingThursday = 1;
        if (day === 'Fridays') inquiry.operatingFriday = 1;
        if (day === 'Saturdays') inquiry.operatingSaturday = 1;
        if (day === 'Sundays') inquiry.operatingSunday = 1;
      });
      inquiry.operationalOthers = form.fields.operationalOthers.value;

      inquiry.businessNature = form.fields.natureOfBusiness.value;
      inquiry.businessStatus = form.fields.businessStatus.value;
      inquiry.operationalHours = form.fields.operationalHours.value;

      if (inquiry.businessStatus === 1) { // Existing Business
        inquiry.businessName = form.fields.clientCompanyName.value;
        inquiry.businessBuildingName = form.fields.clientBuildingName.value;
        inquiry.businessBuildingUnit = form.fields.clientFloorUnit.value;
        inquiry.businessBuildingLocation = form.fields.clientLocation.value;
      }

      const bldgClass = form.fields.buildingClass.value;
      if (bldgClass === 'Premium') {
        inquiry.classPremium = 1;
      } else if (bldgClass === 'Class A') {
        inquiry.classA = 1
      } else if (bldgClass === 'Class B') {
        inquiry.classB = 1;
      } else if (bldgClass === 'Class C') {
        inquiry.classC = 1;
      }
    }

    inquiry.locations = {
      locations: fields.locations.value
    };

    // People want at least X sqm of space
    inquiry.floorArea = {
      min: fields.minFloorArea.value && parseFloat(fields.minFloorArea.value),
      max: (fields.maxFloorArea.value && parseFloat(fields.maxFloorArea.value)) || undefined
    };

    inquiry.lotArea = {
      min: fields.minLotArea.value && parseFloat(fields.minLotArea.value),
      max: (fields.maxLotArea.value && parseFloat(fields.maxLotArea.value)) || undefined
    };

    // People want to only pay up to Y pesos for their property.
    inquiry.budget = {
      min: (fields.minPrice.value && parseFloat(fields.minPrice.value)) || undefined,
      max: fields.maxPrice.value && parseFloat(fields.maxPrice.value)
    };

    inquiry.dateViewing = formatWithTimezone(form.fields.viewingDate.value);
    inquiry.dateMoveIn = formatWithTimezone(form.fields.moveInDate.value);
  }

  return inquiry;
}
